import { ApolloError } from '@apollo/client';

const HP_ERROR_CODES = new Set(['HP_BACKEND_ERROR', 'BAD_USER_INPUT']);

/**
 * Extracts a human-readable backend error message from an Apollo error
 * thrown by one of the hanging-protocol mutations (`updateHangingProtocol`,
 * `updateHangingProtocolStep`, `createHangingProtocolStep`,
 * `deleteHangingProtocolStep`).
 *
 * The GraphQL resolver attaches the backend-api's `detail` or flattened DRF
 * validation message as the error's `message` field, with `extensions.code`
 * set to either `HP_BACKEND_ERROR` (server-side outage) or `BAD_USER_INPUT`
 * (client-side validation failure). See `toHPError` in
 * `graphql/src/resolvers/hangingProtocol.ts`.
 *
 * Returns `null` when no usable message is present, so callers can fall back
 * to their static "Please try again" copy.
 */
export function extractHPBackendErrorMessage(err: unknown): string | null {
  if (!(err instanceof ApolloError)) return null;
  const first = err.graphQLErrors[0];
  if (!first) return null;
  const code = first.extensions?.code;
  if (typeof code !== 'string' || !HP_ERROR_CODES.has(code)) return null;
  const message = typeof first.message === 'string' ? first.message.trim() : '';
  return message.length > 0 ? message : null;
}

/**
 * Builds a user-facing failure toast message that combines a short
 * action-specific prefix with the backend detail when available. Falls back
 * to the static factory copy (e.g. "Failed to save step. Please try again.")
 * when no backend detail is present.
 *
 * @example
 *   composeHPFailureMessage({
 *     action: 'save step',
 *     fallback: CLINICAL_SAVE_FAILED_TOAST.message,
 *     error,
 *   })
 *   // → "Failed to save step: Steps must have unique order values." (with detail)
 *   // → "Failed to save step. Please try again."                    (without detail)
 */
export function composeHPFailureMessage({
  action,
  fallback,
  error,
}: {
  action: string;
  fallback: string;
  error: unknown;
}): string {
  const detail = extractHPBackendErrorMessage(error);
  return detail != null ? `Failed to ${action}: ${detail}` : fallback;
}
